import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {Dialog} from "primereact/dialog";
import {AppContext} from "../../HomePage";
import {SystemHealthService} from "../../service/SystemHealthService";
import AdminPageHeader from "./AdminPageHeader";
import SystemHealthTiles, {DOMAIN_LINKS, STATUS_STYLE, SystemHealthStatusBar}
    from "./SystemHealthTiles";
import {toDateTimeString, toRelativeTimeString} from "../../utils/TimeUtil";
import './SystemHealth.css';

// Unified admin health screen: a one-line status bar over domain tiles (worst
// active severity, alert counts and headline metrics from the evaluator
// snapshot) over the active-alert table, with timed mutes, a muted section and
// a recently-resolved section. Opening the screen marks all alerts seen (drives
// the topbar bell badge). Auto-refreshes while open.
const REFRESH_MS = 30 * 1000;

const MUTE_OPTIONS_HOURS = [1, 4, 24, 168, 720];

// An alert's "entity" is whatever the rule keys its fingerprint on, which for
// some rules is an opaque id: an exception signature is a 64-char hash, a
// campaign or user is a Mongo ObjectId. The row shows the human name the rule
// already put in context and keeps the id underneath, so the column identifies
// something instead of printing a hash nobody can read.
const ENTITY_LABEL = {
    new_exception_hash: (context) => context.host,
    marketplace_connection_error: (context) => context.campaign_name,
    campaign_failure_spike: (context) => context.campaign_name,
    campaign_job_slow: (context) => context.campaign_name,
    products_not_captured: (context) => context.campaign_name,
    dormant_paid_user: (context) => context.user_email,
    ai_job_stuck: (context) => [context.repo, context.operation].filter(Boolean).join('/'),
};

const OPAQUE_ID = /^[0-9a-f]{24,}$/i;

const shortenId = (entity) => (entity && OPAQUE_ID.test(entity))
    ? `${entity.slice(0, 8)}…` : entity;

// Context keys already spelled out in the entity column or the detail line;
// repeating them in the expansion is noise.
const CONTEXT_SKIP = new Set(['campaign_name', 'user_email', 'host', 'exception']);


const SystemHealth = () => {

    const {t, toast, setError} = React.useContext(AppContext);
    const service = useRef(new SystemHealthService()).current;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [domainFilter, setDomainFilter] = useState(null);
    // One expansion state per table: sharing a single one made expanding a
    // muted row collapse whatever was open in the active table.
    const [expandedRows, setExpandedRows] = useState(null);
    const [expandedMutedRows, setExpandedMutedRows] = useState(null);
    const [showMuted, setShowMuted] = useState(false);
    const [showResolved, setShowResolved] = useState(false);
    const [rulesOpen, setRulesOpen] = useState(false);
    const [rules, setRules] = useState(null);
    const markedSeen = useRef(false);

    const load = useCallback(() => {
        setLoading(true);
        service.getOverview(1)
            .then(overview => {
                setData(overview);
                if (!markedSeen.current) {
                    markedSeen.current = true;
                    service.markSeen().catch(() => {});
                }
            })
            .catch(error => { setError(error); setError(null); })
            .finally(() => setLoading(false));
    }, [service, setError]);

    const loadRules = useCallback(() => service.getRules()
        .then(data => setRules(data.rules))
        .catch(error => { setError(error); setError(null); }), [service, setError]);

    useEffect(() => {
        load();
        const timer = setInterval(load, REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    // Loaded once, not on every refresh: the catalog only supplies each rule's
    // thresholds so an expanded row can spell out what actually fires it.
    useEffect(() => { loadRules(); }, [loadRules]);

    const rulesByKey = useMemo(() => Object.fromEntries(
        (rules || []).map(rule => [rule.rule_key, rule])), [rules]);

    const cadenceLabel = (minutes) => t(`system_health.cadence.${minutes}`,
        {defaultValue: `${minutes} min`});

    const ruleName = (ruleKey) => t(`system_health.rule.${ruleKey}.name`,
        {defaultValue: ruleKey});

    // Rule text: i18n by rule_key with the stored English title as fallback.
    const alertTitle = (alert) => t(`system_health.rule.${alert.rule_key}.title`,
        {...alert.context, entity: alert.entity, defaultValue: alert.title});

    const ruleCondition = (ruleKey) => {
        const rule = rulesByKey[ruleKey];
        return rule ? t(`system_health.rule.${ruleKey}.condition`,
            {...rule.thresholds, defaultValue: ''}) : '';
    };

    const ruleMuteControl = (rule) => rule.muted
        ? <Button className="p-button-text p-button-sm" icon="pi pi-volume-up"
                  label={t('system_health.unmute')}
                  onClick={() => service.unmute(rule.rule_key).then(() => { loadRules(); load(); })} />
        : <Dropdown className="p-inputtext-sm" placeholder={t('system_health.mute_rule')}
                    options={MUTE_OPTIONS_HOURS.map(h => ({
                        label: t(`system_health.mute_hours.${h}`), value: h}))}
                    onChange={(e) => service.mute(rule.rule_key, e.value).then(() => {
                        toast.current.show({severity: 'info',
                            summary: t('system_health.rule_muted_toast')});
                        loadRules(); load();
                    })} />;

    const severityTag = (alert) => <Tag severity={alert.severity === 'critical' ? 'danger' : 'warning'}
        value={t(`system_health.severity.${alert.severity}`)} />;

    // Relative in the cell, exact on hover: "8/28/2026, 7:08:27 PM" takes a
    // second read to place, "2 hours ago" takes none.
    const whenColumn = (value) => <span className="sh-when" title={toDateTimeString(value)}>
        {value ? toRelativeTimeString(value) : '—'}</span>;

    const entityColumn = (alert) => {
        const label = (ENTITY_LABEL[alert.rule_key] || (() => null))(alert.context || {});
        const id = shortenId(alert.entity);
        return (
            <div className="sh-entity" title={alert.entity || ''}>
                <span>{label || id || '—'}</span>
                {label && id && label !== id && <span className="sh-entity-sub">{id}</span>}
            </div>);
    };

    const muteControl = (alert) => alert.muted
        ? <Button className="p-button-text p-button-sm" icon="pi pi-volume-up"
                  label={t('system_health.unmute')}
                  onClick={() => service.unmute(alert.fingerprint).then(load)} />
        : <Dropdown className="p-inputtext-sm" placeholder={t('system_health.mute')}
                    options={MUTE_OPTIONS_HOURS.map(h => ({
                        label: t(`system_health.mute_hours.${h}`), value: h}))}
                    onChange={(e) => service.mute(alert.fingerprint, e.value).then(() => {
                        toast.current.show({severity: 'info',
                            summary: t('system_health.muted_toast')});
                        load();
                    })} />;

    // Triage without this meant reading the row, working out which area it
    // belonged to, scrolling back up and clicking that tile's link.
    const actionsColumn = (alert) => (
        <div className="p-d-flex p-ai-center">
            <a href={DOMAIN_LINKS[alert.domain] || '#/management'}
               className="p-button p-button-text p-button-sm"
               title={t('system_health.open_screen')}>
                <i className="pi pi-external-link" />
            </a>
            {muteControl(alert)}
        </div>);

    const rowExpansion = (alert) => (
        <div className="sh-expansion">
            <div className="sh-expansion-detail">{alertTitle(alert)}</div>
            <div className="sh-context">
                {Object.entries(alert.context || {})
                    .filter(([key, value]) => !CONTEXT_SKIP.has(key)
                        && value !== null && value !== undefined && value !== '')
                    .map(([key, value]) =>
                        <div className="sh-context-row" key={key}>
                            <span className="sh-context-key">{key}</span>
                            <span className="sh-context-value">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                        </div>)}
            </div>
            {ruleCondition(alert.rule_key) &&
                <div className="sh-condition">
                    <b>{t('system_health.col_condition')}:</b> {ruleCondition(alert.rule_key)}
                </div>}
            {alert.muted && alert.muted_until &&
                <div className="sh-condition">
                    <b>{t('system_health.muted_label')}:</b> {toDateTimeString(alert.muted_until)}
                </div>}
        </div>);

    const alertTable = (rows, emptyMessage, expanded, setExpanded) => (
        <DataTable value={rows} dataKey="id" loading={loading}
                   paginator={rows.length > 50} rows={50}
                   emptyMessage={emptyMessage}
                   expandedRows={expanded} onRowToggle={(e) => setExpanded(e.data)}
                   rowExpansionTemplate={rowExpansion}
                   rowClassName={(row) => ({'sh-row-muted': row.muted})}>
            <Column expander={true} headerStyle={{width: '3em'}} />
            <Column header={t('system_health.col_severity')} body={severityTag} style={{width: '8em'}} />
            <Column field="rule_key" header={t('system_health.col_rule')}
                    body={(row) => ruleName(row.rule_key)} style={{width: '13em'}} />
            <Column header={t('system_health.col_detail')}
                    body={(row) => <div className="sh-detail">{alertTitle(row)}</div>} />
            <Column header={t('system_health.col_entity')} body={entityColumn} style={{width: '14em'}} />
            <Column header={t('system_health.col_first_seen')}
                    body={(row) => whenColumn(row.first_seen_at)} style={{width: '9em'}} />
            <Column header={t('system_health.col_last_seen')}
                    body={(row) => whenColumn(row.last_seen_at)} style={{width: '9em'}} />
            <Column header="" body={actionsColumn} style={{width: '13em'}} />
        </DataTable>);

    const sectionToggle = (open, setOpen, label, count) => (
        <button type="button" className="sh-section-toggle" onClick={() => setOpen(!open)}>
            <i className={open ? 'pi pi-chevron-down' : 'pi pi-chevron-right'} />
            {label} <span className="sh-section-count">({count})</span>
        </button>);

    const tiles = (data && data.tiles) || {};
    const virtualAlerts = (data && data.virtual_alerts) || [];
    const allAlerts = (data && data.alerts) || [];
    const resolved = (data && data.resolved) || [];

    // Muted alerts leave the main table entirely. A critical that has been
    // muted for a week used to sit at the top -- reserved the most visual
    // weight for the one row nobody was going to act on, while its own tile
    // (correctly) stayed green.
    const activeAlerts = allAlerts.filter(alert => !alert.muted);
    const mutedAlerts = allAlerts.filter(alert => alert.muted);

    const visibleAlerts = domainFilter
        ? activeAlerts.filter(alert => alert.domain === domainFilter)
        : activeAlerts;

    const counts = {
        critical: activeAlerts.filter(a => a.severity === 'critical').length
            + virtualAlerts.filter(a => a.severity === 'critical').length,
        warning: activeAlerts.filter(a => a.severity === 'warning').length
            + virtualAlerts.filter(a => a.severity === 'warning').length,
        muted: mutedAlerts.length,
    };

    return (
        <div className="card management-page">
            <AdminPageHeader title={t('menu_item.system_health')}
                             subtitle={t('menu.management_hub.page.system_health')}
                             actions={<React.Fragment>
                                 <Button icon="pi pi-book" className="p-button-text"
                                         label={t('system_health.rules_button')}
                                         onClick={() => { setRulesOpen(true); loadRules(); }} />
                                 <Button icon="pi pi-refresh" className="p-button-text"
                                         loading={loading} onClick={load} />
                             </React.Fragment>} />

            {virtualAlerts.length > 0 &&
                <div style={{padding: '0.75em 1em', marginBottom: '1em', borderRadius: 8,
                             background: STATUS_STYLE.critical.bg, color: STATUS_STYLE.critical.color,
                             fontWeight: 700}}>
                    <i className="pi pi-exclamation-circle" style={{marginRight: 8}} />
                    {virtualAlerts.map(v => alertTitle(v)).join(' — ')}
                </div>}

            <SystemHealthStatusBar tiles={tiles} counts={counts}
                                   lastRunAt={data && data.meta ? data.meta.last_run_at : null} />

            <SystemHealthTiles tiles={tiles} selected={domainFilter} onSelect={setDomainFilter} />

            {domainFilter &&
                <div className="sh-filter-bar">
                    <span className="sh-chip">
                        {t('system_health.filtered_by')}: {t(`system_health.domain.${domainFilter}`)}
                        <button type="button" onClick={() => setDomainFilter(null)}
                                aria-label={t('system_health.clear_filter')}>
                            <i className="pi pi-times" />
                        </button>
                    </span>
                </div>}

            {alertTable(visibleAlerts, t('system_health.no_alerts'),
                expandedRows, setExpandedRows)}

            {mutedAlerts.length > 0 && <React.Fragment>
                {sectionToggle(showMuted, setShowMuted, t('system_health.muted_title'),
                    mutedAlerts.length)}
                {showMuted && alertTable(mutedAlerts, t('system_health.no_alerts'),
                    expandedMutedRows, setExpandedMutedRows)}
            </React.Fragment>}

            {sectionToggle(showResolved, setShowResolved, t('system_health.resolved_title'),
                resolved.length)}
            {showResolved &&
                <DataTable value={resolved} dataKey="id"
                           emptyMessage={t('system_health.no_resolved')}>
                    <Column header={t('system_health.col_severity')} body={severityTag}
                            style={{width: '8em'}} />
                    <Column header={t('system_health.col_rule')}
                            body={(row) => ruleName(row.rule_key)} style={{width: '13em'}} />
                    <Column header={t('system_health.col_detail')}
                            body={(row) => <div className="sh-detail">{alertTitle(row)}</div>} />
                    <Column header={t('system_health.col_entity')} body={entityColumn}
                            style={{width: '14em'}} />
                    <Column header={t('system_health.col_resolved_at')}
                            body={(row) => whenColumn(row.resolved_at)} style={{width: '9em'}} />
                </DataTable>}

            <Dialog header={t('system_health.rules_title')} visible={rulesOpen}
                    style={{width: '72rem', maxWidth: '95vw'}} onHide={() => setRulesOpen(false)}>
                <DataTable value={rules || []} dataKey="rule_key" loading={rules === null}
                           rowClassName={(row) => ({'sh-row-muted': row.muted})}>
                    <Column header={t('system_health.col_rule')}
                            body={(row) => ruleName(row.rule_key)} style={{width: '13rem'}} />
                    <Column header={t('system_health.col_domain')}
                            body={(row) => t(`system_health.domain.${row.domain}`)}
                            style={{width: '8rem'}} />
                    <Column header={t('system_health.col_condition')}
                            body={(row) => ruleCondition(row.rule_key)} />
                    <Column header={t('system_health.col_cadence')}
                            body={(row) => cadenceLabel(row.cadence_minutes)} style={{width: '7rem'}} />
                    <Column header={t('system_health.col_last_run')}
                            body={(row) => <span title={toDateTimeString(row.last_run_at)}>
                                {row.last_run_at ? toRelativeTimeString(row.last_run_at) : '—'}{' '}
                                {row.last_run_ok === false &&
                                    <Tag severity="danger" value={t('system_health.run_failed')} />}
                            </span>} style={{width: '11rem'}} />
                    <Column header={t('system_health.col_active')}
                            body={(row) => row.active_alerts > 0
                                ? <Tag severity="warning" value={row.active_alerts} /> : '—'}
                            style={{width: '5rem'}} />
                    <Column header="" body={ruleMuteControl} style={{width: '11rem'}} />
                </DataTable>
                <div style={{marginTop: '0.75em', fontSize: '.78rem', opacity: .7}}>
                    {t('system_health.rules_footnote')}
                </div>
            </Dialog>
        </div>
    );
};
export default SystemHealth;
